import { ChevronDown } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { cn } from "@/utils";
import { OptionsList } from "./DropdownOptions";
import type { DropdownOption, DropdownOptionGroup } from "./dropdownTypes";

export type { DropdownOption } from "./dropdownTypes";

interface DropdownProps extends Omit<React.ComponentPropsWithRef<"div">, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  disabled?: boolean;
  placeholder?: string;
  /** Size variant: 'md' (default) matches existing Dropdown sizing, 'sm' matches compact filter panels. */
  size?: "sm" | "md";
  /** When provided, options are rendered grouped under section headers instead of flat. */
  groups?: DropdownOptionGroup[];
  /** Optional prefix element rendered before each option label in the dropdown list. */
  renderOptionPrefix?: (option: DropdownOption) => React.ReactNode;
  /** Optional prefix element rendered before the selected value display. */
  renderSelectedPrefix?: (option: DropdownOption) => React.ReactNode;
  /** When true, opens the dropdown on mount. Used by click-to-edit patterns. */
  autoOpen?: boolean;
  /**
   * When true, the listbox renders via portal with ``position: fixed`` so it
   * escapes any ``overflow: hidden|auto`` ancestor. Use inside modals,
   * scrolling tables, or anywhere the default ``position: absolute``
   * listbox would be clipped.
   */
  portal?: boolean;
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(function Dropdown(
  {
    value,
    onChange,
    options,
    disabled = false,
    placeholder = "Select...",
    className,
    size = "md",
    groups,
    renderOptionPrefix,
    renderSelectedPrefix,
    autoOpen = false,
    portal = false,
    ...props
  },
  ref,
) {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  // Default close-on-outside-click. Disabled in portal mode because the
  // listbox lives outside ``dropdownRef`` in the DOM; we handle it manually.
  useClickOutside(dropdownRef, closeDropdown, isOpen && !portal);

  // Portal mode: close on outside click, accounting for clicks inside the
  // portalled listbox as "inside".
  useEffect(() => {
    if (!isOpen || !portal) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      if (listboxRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, portal]);

  // Portal mode: track trigger position so the fixed listbox follows it
  // during scroll/resize while open.
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!isOpen || !portal) {
      setTriggerRect(null);
      return;
    }
    const updateRect = () => {
      if (dropdownRef.current) {
        setTriggerRect(dropdownRef.current.getBoundingClientRect());
      }
    };
    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [isOpen, portal]);

  // Build a flat list of selectable options for keyboard navigation.
  // When groups are provided, the flat list is derived from groups (skipping group headers).
  const flatOptions = useMemo(() => {
    if (!groups) return options;
    return groups.flatMap((g) => g.options);
  }, [groups, options]);

  const handleSelect = useCallback(
    (index: number) => {
      const option = flatOptions[index];
      if (!option) return;
      onChange(option.value);
      setIsOpen(false);
    },
    [onChange, flatOptions],
  );

  const { highlightedIndex, getKeyboardProps } = useKeyboardNavigation({
    itemCount: flatOptions.length,
    isOpen,
    onSelect: handleSelect,
    onClose: closeDropdown,
  });

  const selectedOption =
    options.find((opt) => opt.value === value) ??
    (groups ? groups.flatMap((g) => g.options).find((opt) => opt.value === value) : undefined);

  const activeDescendant =
    highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined;

  // Size-based classes
  const isSm = size === "sm";
  const triggerSizeClasses = isSm ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm";
  const optionSizeClasses = isSm ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm";
  const chevronSize = isSm ? 12 : 14;
  const maxHeight = isSm ? "max-h-48" : "max-h-60";

  return (
    <div className={cn("relative", className)} ref={ref ?? dropdownRef} {...props}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        {...getKeyboardProps()}
        className={cn(
          "w-full text-left border border-border-secondary rounded-md focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-transparent disabled:bg-bg-secondary disabled:text-text-tertiary flex items-center justify-between transition-colors",
          triggerSizeClasses,
          value ? "bg-bg-secondary hover:bg-bg-secondary" : "bg-bg-primary hover:bg-bg-secondary",
        )}
      >
        <span
          className={cn(
            "flex items-center gap-2",
            value ? "text-text-primary" : "text-text-tertiary",
          )}
        >
          {selectedOption && renderSelectedPrefix?.(selectedOption)}
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          size={chevronSize}
          className={cn(
            "text-text-tertiary transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && !disabled && !portal && (
        <div
          id={listboxId}
          ref={listboxRef}
          role="listbox"
          className={cn(
            "absolute z-popover w-full mt-1 bg-bg-primary border border-border-secondary rounded-md shadow-lg overflow-auto",
            maxHeight,
          )}
        >
          <OptionsList
            groups={groups}
            options={options}
            listboxId={listboxId}
            value={value}
            sizeClasses={optionSizeClasses}
            highlightedIndex={highlightedIndex}
            onChange={onChange}
            onClose={closeDropdown}
            renderOptionPrefix={renderOptionPrefix}
          />
        </div>
      )}
      {isOpen &&
        !disabled &&
        portal &&
        triggerRect &&
        createPortal(
          <div
            id={listboxId}
            ref={listboxRef}
            role="listbox"
            className={cn(
              "fixed z-modal-dropdown bg-bg-primary border border-border-secondary rounded-md shadow-lg overflow-auto",
              maxHeight,
            )}
            style={{
              top: triggerRect.bottom + 4,
              left: triggerRect.left,
              width: triggerRect.width,
            }}
          >
            <OptionsList
              groups={groups}
              options={options}
              listboxId={listboxId}
              value={value}
              sizeClasses={optionSizeClasses}
              highlightedIndex={highlightedIndex}
              onChange={onChange}
              onClose={closeDropdown}
              renderOptionPrefix={renderOptionPrefix}
            />
          </div>,
          document.body,
        )}
    </div>
  );
});
